import { system, reactive, effects, listen } from 'kho';
import {
  Box3,
  BufferAttribute,
  BufferGeometry,
  CircleGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  DoubleSide,
  Group,
  Line,
  LineBasicMaterial,
  LineLoop,
  Matrix3,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Plane,
  Quaternion,
  Ray,
  Raycaster,
  ShaderMaterial,
  TorusGeometry,
  Vector2,
  Vector3,
} from 'three';
import {
  $camera,
  $frame,
  $renderer,
  $scene,
} from '../data/scene';
import { $cloudScenes } from '../data/cloud';
import {
  $dragDanger,
  $editorMode,
  $gizmoInfo,
  $instances,
  $instancesGroup,
  $pickRadius,
  $selection,
  $transformRequest,
  $deleteRequest,
  type Instance,
} from '../data/model';
import { $objectScale } from '../data/ifc';
import { $surfaceSampler } from '../data/surface';
import type { Quat, Vec3 } from '../data/model';
import type { Camera, WebGLRenderer } from 'three';

/**
 * World units per screen pixel at the mesh, so the gizmo can hold a constant
 * on-screen size. Handles both ortho (AR view) and perspective cameras.
 */
function worldPerPixel(
  cam: Camera | null,
  renderer: WebGLRenderer | null,
  mesh: Mesh,
): number {
  if (!cam || !renderer) return 0;
  const el = renderer.domElement;
  const h = el?.clientHeight || el?.height || 900;
  const c = cam as any;
  if (c.isOrthographicCamera) {
    return (c.top - c.bottom) / (c.zoom || 1) / h;
  }
  if (c.isPerspectiveCamera) {
    const d = c.position.distanceTo(mesh.position);
    return (2 * d * Math.tan((c.fov * Math.PI) / 360)) / h;
  }
  return 0;
}

const UP = new Vector3(0, 0, 1);
const DOWN = new Vector3(0, 0, -1);
const RING_PX = 110; // base-plate radius in screen pixels (never smaller than the object)
const PATH_N = 8; // top-down casts per direction (forward AND backward)
const PATH_SUB = 5; // ribbon subdivisions between casts
const PATH_VERTS = 2 * PATH_N * PATH_SUB + 1;

type HandleName =
  | 'dir0' | 'dir1' | 'dir2' | 'dir3' // 90° arrows → directed drag
  | 'rotObj' // 45°: yaw base + object
  | 'rotBase' // 135°: yaw base plate only (re-aims the arrows)
  | 'free' // 225°: free move (the disc body free-moves too)
  | 'height' // top: slide along `up`
  | 'tiltX' | 'tiltY'; // rotation rings around the base X/Y axes (child tilt)

/** Base-plate frame: surface alignment × yaw around the normal. */
const baseQuatOf = (up: Vector3, yaw: number) =>
  new Quaternion()
    .setFromUnitVectors(UP, up)
    .multiply(new Quaternion().setFromAxisAngle(UP, yaw));

/** Param t of the closest point on line (origin + t·dir) to `ray`. */
function axisParam(ray: Ray, origin: Vector3, dir: Vector3): number {
  const w0 = new Vector3().subVectors(origin, ray.origin);
  const b = dir.dot(ray.direction);
  const d = dir.dot(w0);
  const e = ray.direction.dot(w0);
  const denom = 1 - b * b; // dir & ray.direction are unit
  if (Math.abs(denom) < 1e-8) return 0;
  return (b * e - d) / denom;
}

/**
 * BASE-PLATE gizmo (fully custom, drives everything through $transformRequest).
 *
 * Base (đế) at the object's foot, oriented by (up, baseYaw):
 *  - 4 arrows at 0/90/180/270° → DIRECTED drag along that axis (solid fading
 *    guideline both ways; red segments where the top-down cast finds a hole).
 *  - 45°  rotObj  — yaw the object AND the base together.
 *  - 135° rotBase — yaw only the base plate (re-aims the arrows).
 *  - 225° free    — free move on the surface (disc body drags freely too).
 * Top (đỉnh) above the object:
 *  - height — slide the object along its up axis (can lift off the surface).
 *  - tilt   — phi/theta tilt around the base pivot; the base stays planted.
 *
 * Surface drags land via top-down casts; the object's relative orientation is
 * parallel-transported (align(oldUp→newUp) ∘ q). Probing a hole turns the
 * guideline red, wraps the object in animated 45° red/white hazard stripes,
 * and releasing there DELETES the instance.
 */
export const gizmoSystem = system((scope) => {
  const { atoms, maps } = scope(reactive);
  const { effect, onDispose } = scope(effects);
  const { on, emit } = scope(listen);

  // ---------------------------------------------------------------- visuals
  // Occlusion by the SELECTED OBJECT only: its depth is rendered at half res
  // (rendererSystem) and every gizmo material samples it with a 5-tap PCF —
  // soft edges, no depth clear, and half the occluder fill cost.
  const occUniforms = {
    uOccDepth: { value: null as import('three').Texture | null },
    uOccTexel: { value: new Vector2(1, 1) },
    uFullSize: { value: new Vector2(1, 1) },
    uHasOcc: { value: 0 },
  };
  const GIZMO_FRAG = /* glsl */ `
    uniform vec3 uColor;
    uniform float uOpacity;
    uniform sampler2D uOccDepth;
    uniform vec2 uOccTexel;
    uniform vec2 uFullSize;
    uniform float uHasOcc;
    void main() {
      float occ = 0.0;
      if (uHasOcc > 0.5) {
        vec2 uv = gl_FragCoord.xy / uFullSize;
        float z = gl_FragCoord.z - 0.0005;
        occ += step(texture2D(uOccDepth, uv).r, z);
        occ += step(texture2D(uOccDepth, uv + vec2( uOccTexel.x,  uOccTexel.y)).r, z);
        occ += step(texture2D(uOccDepth, uv + vec2(-uOccTexel.x,  uOccTexel.y)).r, z);
        occ += step(texture2D(uOccDepth, uv + vec2( uOccTexel.x, -uOccTexel.y)).r, z);
        occ += step(texture2D(uOccDepth, uv + vec2(-uOccTexel.x, -uOccTexel.y)).r, z);
        occ /= 5.0; // PCF blur → soft occlusion edge
      }
      float a = uOpacity * (1.0 - occ);
      if (a < 0.003) discard;
      gl_FragColor = vec4(uColor, a);
    }
  `;
  const gizmoUniforms = (color: number, opacity: number) => ({
    ...occUniforms, // shared references — updated once for all materials
    uColor: { value: new Color(color) },
    uOpacity: { value: opacity },
  });
  const solid = (color: number, opacity = 0.95, side = 0) =>
    new ShaderMaterial({
      uniforms: gizmoUniforms(color, opacity),
      vertexShader: /* glsl */ `
        void main() { gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
      `,
      fragmentShader: GIZMO_FRAG,
      transparent: true,
      depthTest: false, // occlusion comes entirely from the sampled texture
      depthWrite: false,
      ...(side === 2 ? { side: DoubleSide } : {}),
    });
  /**
   * Same drawing algorithm as the travel guideline: a camera-facing ribbon
   * expanded in the vertex shader, with per-vertex width — so the axis arrows
   * and the guideline share one visual language (flat, screen-facing).
   */

  const baseGroup = new Group();
  const disc = new Mesh(new CircleGeometry(1, 48), solid(0x66d9ff, 0.16, 2));
  disc.renderOrder = 996;
  disc.userData.handle = 'free' satisfies HandleName; // disc body = free move
  const outlineGeo = new BufferGeometry();
  {
    const pts = new Float32Array(64 * 3);
    for (let i = 0; i < 64; i++) {
      const a = (i / 64) * Math.PI * 2;
      pts.set([Math.cos(a), Math.sin(a), 0], i * 3);
    }
    outlineGeo.setAttribute('position', new BufferAttribute(pts, 3));
  }
  const outline = new LineLoop(outlineGeo, solid(0x66d9ff, 0.8));
  outline.renderOrder = 997;
  baseGroup.add(disc, outline);

  const invisible = () =>
    new MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false });

  // 4 directed-drag arrows at 0/90/180/270°. The VISIBLE arrows are cast-built
  // terrain-following ribbons (see buildArrows); their GRAB zones are invisible
  // hit-LINES updated to the SAME cast polyline, so you grab exactly what you
  // see (raycast uses a Line distance threshold ≈ the ribbon width).
  const handleMeshes: Object3D[] = [disc];

  // rotate handles: THICK RED arc = rotObj (object+base), THIN BLUE arc = rotBase
  const arcHandle = (
    centerDeg: number,
    spanDeg: number,
    tube: number,
    color: number,
    handle: HandleName,
  ) => {
    const A = (spanDeg * Math.PI) / 180;
    const g = new Group();
    const vis = new Mesh(new TorusGeometry(1.0, tube, 8, 32, A), solid(color));
    vis.renderOrder = 998;
    const hit = new Mesh(new TorusGeometry(1.0, 0.14, 6, 24, A), invisible());
    for (const m of [vis, hit]) {
      m.userData.handle = handle;
      g.add(m);
      handleMeshes.push(m);
    }
    g.rotation.z = A / 2 - (centerDeg * Math.PI) / 180;
    baseGroup.add(g);
  };
  arcHandle(45, 75, 0.06, 0xff4040, 'rotObj'); // thick, red — spins object + base
  arcHandle(135, 75, 0.026, 0xffc61a, 'rotBase'); // thinner, yellow — spins the base only

  // free-move is grabbed via the disc body / the object itself — no extra knob.

  // CHILD handles: two ORTHOGONAL ROTATION RINGS (traditional gizmo) sized to
  // the object's large diagonal wrap the object and tilt it around the base
  // X/Y axes; a STANDING ARROW above lifts it along up. Both move the object
  // while the PARENT pivot stays planted on the surface.
  const ringsGroup = new Group(); // unit radius, scaled to the bbox diagonal/2
  // TorusGeometry's native ring-axis is +Z (base up/yaw axis) — a ring left on
  // +Z would YAW the base. The two child-tilt rings must be around local X and
  // local Y: rotate the circle around Y (→ ring-axis X) or around X (→ Y).
  const rotRing = (axisX: boolean, color: number, handle: HandleName) => {
    const vis = new Mesh(new TorusGeometry(1, 0.016, 8, 64), solid(color));
    const hit = new Mesh(new TorusGeometry(1, 0.1, 6, 32), invisible());
    for (const m of [vis, hit]) {
      const g = m.geometry.clone();
      if (axisX) g.rotateY(Math.PI / 2); // ring-axis local X (circle in local YZ)
      else g.rotateX(Math.PI / 2); // ring-axis local Y (circle in local XZ)
      m.geometry = g;
      m.renderOrder = 998;
      m.userData.handle = handle;
      ringsGroup.add(m);
      handleMeshes.push(m);
    }
  };
  rotRing(true, 0xff5f5f, 'tiltX'); // red — ring-axis local X
  rotRing(false, 0x5fa8ff, 'tiltY'); // blue — ring-axis local Y
  const topGroup = new Group();
  const heightHandle = new Group();
  {
    const shaft = new Mesh(
      new CylinderGeometry(0.035, 0.035, 0.4, 8).rotateX(Math.PI / 2).translate(0, 0, 0.2),
      solid(0x8ef58a),
    );
    const tip = new Mesh(
      new ConeGeometry(0.09, 0.22, 12).rotateX(Math.PI / 2).translate(0, 0, 0.51),
      solid(0x8ef58a),
    );
    for (const c of [shaft, tip]) {
      c.renderOrder = 998;
      c.userData.handle = 'height' satisfies HandleName;
      heightHandle.add(c);
      handleMeshes.push(c);
    }
  }
  topGroup.add(heightHandle);

  // ------------------------------------------------ guideline ribbon (path)
  const pathGeo = new BufferGeometry();
  pathGeo.setAttribute('position', new BufferAttribute(new Float32Array(PATH_VERTS * 2 * 3), 3));
  pathGeo.setAttribute('aTangent', new BufferAttribute(new Float32Array(PATH_VERTS * 2 * 3), 3));
  pathGeo.setAttribute('aSide', new BufferAttribute(new Float32Array(PATH_VERTS * 2), 1));
  pathGeo.setAttribute('aFade', new BufferAttribute(new Float32Array(PATH_VERTS * 2), 1));
  pathGeo.setAttribute('aDanger', new BufferAttribute(new Float32Array(PATH_VERTS * 2), 1));
  pathGeo.setAttribute('aWidth', new BufferAttribute(new Float32Array(PATH_VERTS * 2).fill(1), 1));
  {
    const idx = new Uint16Array((PATH_VERTS - 1) * 6);
    for (let i = 0; i < PATH_VERTS - 1; i++) {
      const a = i * 2;
      idx.set([a, a + 1, a + 2, a + 2, a + 1, a + 3], i * 6);
    }
    pathGeo.setIndex(new BufferAttribute(idx, 1));
  }
  // ONE material draws both the guideline AND the direction arrows — identical
  // width, color, depth behavior. aWidth is a per-vertex multiplier (1 on the
  // guideline; shaped on the arrows to form the head).
  const pathMat = new ShaderMaterial({
    uniforms: {
      uColor: { value: new Color(0x66d9ff) },
      uHalfWidth: { value: 0.1 },
      uHighlight: { value: 0 }, // 1 while a dir arrow is hovered → brighten
      // Yaw applied while a rotate handle is dragged. The ribbon is built in
      // WORLD space and bypasses the model matrix, so a group transform does
      // nothing — the spin must be applied here so the arrows turn with the disk.
      uSpin: { value: new Matrix3() }, // identity when not rotating
      uSpinPivot: { value: new Vector3() },
    },
    vertexShader: /* glsl */ `
      attribute vec3 aTangent;
      attribute float aSide;
      attribute float aFade;
      attribute float aDanger;
      attribute float aWidth;
      uniform float uHalfWidth;
      uniform mat3 uSpin;
      uniform vec3 uSpinPivot;
      varying float vFade;
      varying float vDanger;
      void main() {
        vFade = aFade;
        vDanger = aDanger;
        // spin the world-space vertex + tangent around the pivot (identity = no-op)
        vec3 wpos = uSpinPivot + uSpin * (position - uSpinPivot);
        vec3 tang = uSpin * aTangent;
        // Widen the ribbon in the GROUND plane (perpendicular to the tangent,
        // around world +Z) so it lies FLAT on the terrain with its face pointing
        // up +Z — a ground decal — instead of billboarding to the camera, which
        // tilted some arrows up ("xoay ngang") in the Z-up top-down/oblique view.
        vec3 up = vec3(0.0, 0.0, 1.0);
        vec3 perp = cross(up, tang);
        float plen = length(perp);
        vec3 dirW = plen > 1e-4 ? perp / plen : vec3(1.0, 0.0, 0.0);
        vec3 offset = dirW * (uHalfWidth * aWidth * aSide);
        gl_Position = projectionMatrix * viewMatrix * vec4(wpos + offset, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor;
      uniform float uHighlight;
      varying float vFade;
      varying float vDanger;
      void main() {
        // hole segments (cast found nothing to land on) turn red
        vec3 col = mix(uColor, vec3(1.0, 0.16, 0.12), step(0.5, vDanger));
        col = mix(col, vec3(1.0), uHighlight * 0.45); // hover → toward white
        gl_FragColor = vec4(col, min(1.0, vFade * (1.0 + uHighlight * 0.7)));
      }
    `,
    transparent: true,
    depthTest: true, // terrain occludes the far path
    depthWrite: false,
    side: DoubleSide,
  });
  const pathLine = new Mesh(pathGeo, pathMat);
  pathLine.frustumCulled = false;
  pathLine.visible = false;

  // ---- direction arrows: cast-built ribbons, same material as the guideline
  // stations along the axis (×gizR) and width multipliers (× guideline width)
  // stations measured in SCREEN PIXELS beyond the plate edge → the arrows have
  // a consistent on-screen length at every zoom (they used to be plate-relative:
  // cramped up close, oversized far away)
  const ARROW_PX = [12, 34, 56, 78, 78, 116];
  const ARROW_W = [1, 1, 1, 1, 2.6, 0.06];
  const AR_N = ARROW_PX.length;
  const arrowsGroup = new Group(); // world space, main render pass (like the path)
  const arrowGeos: BufferGeometry[] = [];
  for (let i = 0; i < 4; i++) {
    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(new Float32Array(AR_N * 2 * 3), 3));
    geo.setAttribute('aTangent', new BufferAttribute(new Float32Array(AR_N * 2 * 3), 3));
    geo.setAttribute('aSide', new BufferAttribute(new Float32Array(AR_N * 2), 1));
    geo.setAttribute('aFade', new BufferAttribute(new Float32Array(AR_N * 2).fill(0.95), 1));
    geo.setAttribute('aDanger', new BufferAttribute(new Float32Array(AR_N * 2), 1));
    geo.setAttribute('aWidth', new BufferAttribute(new Float32Array(AR_N * 2), 1));
    const idx: number[] = [];
    for (let k = 0; k < AR_N - 1; k++) {
      const a = k * 2;
      idx.push(a, a + 1, a + 2, a + 2, a + 1, a + 3);
    }
    geo.setIndex(idx);
    const mesh = new Mesh(geo, pathMat);
    mesh.frustumCulled = false;
    arrowsGroup.add(mesh);
    arrowGeos.push(geo);
  }
  // invisible hit-lines following each arrow's cast polyline (world space)
  const arrowHitLines: Line[] = [];
  const arrowGrab: (Vector3 | null)[] = [null, null, null, null];
  for (let i = 0; i < 4; i++) {
    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(new Float32Array(AR_N * 3), 3));
    const line = new Line(
      geo,
      new LineBasicMaterial({ transparent: true, opacity: 0 }),
    );
    line.frustumCulled = false;
    line.userData.handle = `dir${i}` as HandleName;
    arrowsGroup.add(line);
    arrowHitLines.push(line);
    handleMeshes.push(line);
  }

  // -------------------------------------------- hazard overlay (danger zone)
  // Opaque hazard material swapped ONTO the object's own mesh during "danger"
  // (no geometry clone → zero extra triangles). Renders the solid shaded shape
  // with animated 45° red/white stripes on top. Only one object is ever in
  // danger at a time, so a single shared material is enough.
  const hazardMat = new ShaderMaterial({
    uniforms: { uTime: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec3 vN;
      void main() {
        vN = normalize(normalMatrix * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      varying vec3 vN;
      void main() {
        // cheap view-space shading so the solid shape reads under the stripes
        float lt = 0.42 + 0.58 * abs(vN.z);
        vec3 base = vec3(0.72, 0.72, 0.75) * lt;
        // screen-space 45° stripes, scrolling (looping)
        float band = mod(gl_FragCoord.x + gl_FragCoord.y - uTime * 60.0, 28.0);
        vec3 hz = band >= 14.0 ? vec3(0.95, 0.15, 0.12) : vec3(1.0, 0.92, 0.92);
        gl_FragColor = vec4(mix(base, hz, 0.55), 1.0);
      }
    `,
    side: DoubleSide,
  });
  // object currently shown in hazard state + its meshes' original materials
  // (restored when danger clears). Material-swap only — geometry is untouched.
  let hazardObj: Object3D | null = null;
  const hazardOrigMats = new Map<Mesh, Mesh["material"]>();
  const restoreHazard = () => {
    for (const [m, mat] of hazardOrigMats) m.material = mat;
    hazardOrigMats.clear();
    hazardObj = null;
  };

  // ------------------------------------------------------------------ state
  const raycaster = new Raycaster();
  raycaster.layers.enableAll();
  /** Line-distance tolerance for the arrow hit-lines ≈ visible ribbon width. */
  const setLineThreshold = () => {
    (raycaster.params as { Line: { threshold: number } }).Line.threshold = Math.max(
      pickRadius() * 0.35,
      gizR * 0.08,
    );
  };
  const WHITE = new Color(0xffffff);
  // hover highlight: brighten + solidify the hovered handle's material. Solid
  // handles expose uOpacity/uColor; dir arrows use pathMat.uHighlight instead.
  type HVis = { mat: { uniforms: { uOpacity: { value: number }; uColor: { value: Color } } }; op: number; col: Color };
  const handleVisuals = new Map<string, HVis[]>();
  for (const root of [baseGroup, ringsGroup, topGroup]) {
    root.traverse((o) => {
      const h = o.userData.handle as string | undefined;
      const m = (o as Mesh).material as any;
      if (h && m?.uniforms?.uOpacity && m.uniforms.uColor) {
        const arr = handleVisuals.get(h) ?? [];
        arr.push({ mat: m, op: m.uniforms.uOpacity.value, col: m.uniforms.uColor.value.clone() });
        handleVisuals.set(h, arr);
      }
    });
  }
  let hoveredHandle: string | null = null;
  /** Highlight the hovered handle (null = none); returns true when it changed. */
  const applyHover = (h: string | null): boolean => {
    if (h === hoveredHandle) return false;
    if (hoveredHandle) {
      for (const v of handleVisuals.get(hoveredHandle) ?? []) {
        v.mat.uniforms.uOpacity.value = v.op;
        v.mat.uniforms.uColor.value.copy(v.col);
      }
      if (hoveredHandle.startsWith('dir')) pathMat.uniforms.uHighlight!.value = 0;
    }
    hoveredHandle = h;
    if (h) {
      for (const v of handleVisuals.get(h) ?? []) {
        v.mat.uniforms.uOpacity.value = Math.min(1, v.op * 1.7 + 0.2);
        v.mat.uniforms.uColor.value.copy(v.col).lerp(WHITE, 0.4);
      }
      if (h.startsWith('dir')) pathMat.uniforms.uHighlight!.value = 1;
    }
    return true;
  };

  // Depth-correct handle pick: the CLOSEST *precise* handle wins (arcs, rings,
  // height, dir arrows) so one behind another no longer steals the pick via the
  // old blanket dir-priority; the big flat 'free' disc is only a fallback.
  const PRECISE_HANDLES = new Set([
    'rotObj', 'rotBase', 'tiltX', 'tiltY', 'height', 'dir0', 'dir1', 'dir2', 'dir3',
  ]);
  const pickHandleHit = (ndc: { x: number; y: number }, camera: Camera, obj: Object3D) => {
    raycaster.setFromCamera(new Vector2(ndc.x, ndc.y), camera);
    setLineThreshold();
    const hits = raycaster.intersectObjects(handleMeshes, false);
    const objHit = raycaster.intersectObject(obj, true)[0];
    const precise = hits.filter((x) => PRECISE_HANDLES.has(String(x.object.userData.handle)));
    return { pick: precise[0] ?? hits[0], objHit };
  };

  // The shared surface sampler is owned by surfaceSystem (kho state); read the
  // live instance off $surfaceSampler. Null when no renderer yet → skip sampling.
  const getSampler = () => atoms.get($surfaceSampler);
  let mounted = false;
  let gizR = 1;
  let bboxRadiusWorld = 1;
  let bboxHeight = 1;
  let bboxDiagWorld = 2;
  let lastPathKey = '';
  // memo for buildArrows: skip the ~20-40 GPU surface samples unless the arrow
  // geometry inputs actually changed. forceArrows lets onPointerUp force one
  // rebuild after a drag (arrows were hidden; the object landed on new surface).
  // Split into two keys: objKey = the object transform (rebuild immediately, the
  // arrows must track the object) and viewKey = camera-distance + streamed-terrain
  // signature (rebuild THROTTLED). Each sample() is a synchronous
  // readRenderTargetPixels that stalls the main thread until the GPU drains (incl.
  // the multi-million-point cloud), so re-sampling every frame while the camera
  // orbits or tiles stream in costs 400-900ms/s of readback (measured) and tanks
  // FPS. View-only changes re-hug at ARROW_RESAMPLE_MS, not per frame.
  let lastArrowObjKey = '';
  let lastArrowViewKey = '';
  let lastArrowSampleAt = -1e9;
  const ARROW_RESAMPLE_MS = 200; // camera/terrain-driven re-hug cap (~5 Hz)
  let forceArrows = false;

  let dragging = false;
  let handle: HandleName | null = null;
  let dragId: string | null = null;
  let start: Instance | null = null; // snapshot at grab
  let grabOffset = new Vector3();
  let dragDir: Vector3 | null = null; // directed axis (horizontal) / smoothed free dir
  let startAngle = 0; // rotate handles: screen angle at grab
  let rotSign = 1;
  let tStart = 0; // height handle: axis param at grab
  // rotation-ring drag state
  let tiltAxis = new Vector3(1, 0, 0);
  let tiltCenter = new Vector3();
  let tiltV0: Vector3 | null = null;
  let startNdc = { x: 0, y: 0 };
  let smoothPos: Vector3 | null = null;
  let smoothQuat: Quaternion | null = null;
  let curUp = new Vector3(0, 0, 1);
  let danger = false;
  let mouseNdc: { x: number; y: number } | null = null;
  let dirty = false;
  let gizmoCursor = false; // whether WE set the cursor (don't stomp placement's)

  const canvas = () => atoms.get($renderer)?.domElement ?? null;
  const pickRadius = () => {
    const objectScale = atoms.get($objectScale) ?? 1;
    return atoms.get($pickRadius) ?? objectScale * 1.5;
  };
  const selectedObject = () => {
    const id = atoms.get($selection);
    const group = atoms.get($instancesGroup);
    return id ? (group?.children.find((o) => o.userData.instanceId === id) ?? null) : null;
  };

  /**
   * World bounding box of the selected IFC object (origin-at-base), cached by
   * the instance's IFC url. Falls back to a unit Z-up box until the async IFC
   * mesh has resolved. (POC used templateById(templateId).create().)
   */
  const bboxCache = new Map<string, Box3>();
  const FALLBACK_BBOX = new Box3(new Vector3(-0.5, -0.5, 0), new Vector3(0.5, 0.5, 1));
  const instanceBBox = (inst: Instance): Box3 => {
    const cached = bboxCache.get(inst.url);
    if (cached) return cached;
    const obj = selectedObject();
    if (!obj) return FALLBACK_BBOX;
    const b = new Box3().setFromObject(obj);
    if (b.isEmpty()) return FALLBACK_BBOX;
    bboxCache.set(inst.url, b);
    return b;
  };
  const selectedInstance = (): Instance | null => {
    const id = atoms.get($selection);
    return id ? (maps.get($instances, id) ?? null) : null;
  };

  const toNdc = (e: PointerEvent) => {
    const el = canvas();
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
      return null;
    }
    return {
      x: ((e.clientX - rect.left) / rect.width) * 2 - 1,
      y: -(((e.clientY - rect.top) / rect.height) * 2 - 1),
    };
  };

  const setCursor = (c: string) => {
    const el = canvas();
    if (el && el.style.cursor !== c) el.style.cursor = c;
  };

  /**
   * Top-down cast. `extend` widens the vertical search window (±extend around
   * fromY) — used to RECOVER from the danger state when dragging back over
   * terrain that is much higher/lower than the frozen height.
   */
  const castDown = (
    tx: number,
    ty: number,
    fromZ: number,
    R: number,
    extend = 0,
  ) => {
    const scenes = atoms.get($cloudScenes);
    if (scenes.length === 0) return { origin: new Vector3(tx, ty, fromZ), est: null, z: 0 };
    const castH = Math.max(2 * R, bboxHeight + R) + extend;
    const origin = new Vector3(tx, ty, fromZ + castH);
    const est = getSampler()?.sample(origin, DOWN, R, scenes, 0.5, {
      backoff: 0,
      farMul: (castH + extend + 3 * R) / R,
    });
    if (!est || est.normal.z < 0.15) return { origin, est: null, z: 0 };
    const c = est.centroid;
    const n = est.normal;
    return { origin, est, z: c.z + (n.x * (c.x - tx) + n.y * (c.y - ty)) / n.z };
  };

  /** Base-plate radius: constant RING_PX on screen, clamped ≥ the object footprint. */
  const computeGizR = () => {
    const camera = atoms.get($camera);
    const renderer = atoms.get($renderer);
    if (!camera || !renderer) return Math.max(gizR, bboxRadiusWorld * 1.35);
    const wpp = worldPerPixel(camera, renderer, { position: baseGroup.position } as any);
    return Math.max(RING_PX * wpp, bboxRadiusWorld * 1.35);
  };

  /**
   * Rebuild the 4 direction arrows from chained top-down casts — each arrow is
   * a terrain-hugging segment ribbon, exactly like the guideline is built.
   */
  const buildArrows = (objPos: Vector3, inst: Instance) => {
    // Fresh geometry is built already re-aimed at inst.baseYaw, so drop any
    // drag-time yaw the ribbon shader was applying.
    pathMat.uniforms.uSpin.value.identity();
    pathMat.uniforms.uSpinPivot.value.set(0, 0, 0);
    const R = pickRadius();
    // px → world at the gizmo's distance (constant on-screen arrow length)
    const camera = atoms.get($camera);
    const renderer = atoms.get($renderer);
    const wpp = worldPerPixel(camera, renderer, { position: objPos } as any) || 0.05;
    // Memo guard — skip the ~20-40 castDown/sample() GPU rebuild unless something
    // that shapes the arrows changed. Cloud "version" = total tile-child count
    // across the persistent container scenes (kept in sync with the
    // setCloudScenes guard), so arrows re-hug freshly streamed terrain while an
    // idle-selected frame (or camera orbit) costs 0 samples. height is excluded
    // (arrows hug the surface at inst.position, independent of lift).
    const cloudSig = (atoms.get($cloudScenes) as any[]).reduce(
      (n, s) => n + s.children.length,
      0,
    );
    // objKey = the object's own transform → arrows MUST follow it immediately.
    const objKey = [
      Math.round(objPos.x * 100),
      Math.round(objPos.y * 100),
      Math.round(objPos.z * 100),
      inst.up[0]?.toFixed(3),
      inst.up[1]?.toFixed(3),
      inst.up[2]?.toFixed(3),
      inst.baseYaw.toFixed(4),
    ].join('|');
    // viewKey = camera distance (screen-constant arrow world-size) + streamed
    // terrain → cosmetic re-hug only; throttle so orbiting/streaming while an
    // object is idle-selected does not fire a readback storm every frame.
    const viewKey = [
      Math.round(gizR * 1000),
      Math.round(R * 1000),
      Math.round(wpp * 1e5),
      cloudSig,
    ].join('|');
    const now =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (!forceArrows) {
      if (objKey === lastArrowObjKey) {
        // object stationary: only camera/terrain changed → throttled re-hug
        if (viewKey === lastArrowViewKey) return;
        if (now - lastArrowSampleAt < ARROW_RESAMPLE_MS) return;
      }
    }
    forceArrows = false;
    lastArrowObjKey = objKey;
    lastArrowViewKey = viewKey;
    lastArrowSampleAt = now;
    const lift = R * 0.2;
    const qBase = baseQuatOf(new Vector3().fromArray(inst.up), inst.baseYaw);
    // width relative to the (screen-constant) plate radius, not the world-sized
    // pickRadius — otherwise arrows go thread-thin on a large point cloud. Now
    // that arrows lie flat on the ground they also foreshorten, so keep them fat.
    pathMat.uniforms.uHalfWidth!.value = gizR * 0.07;
    for (let i = 0; i < 4; i++) {
      const a = (i * Math.PI) / 2;
      const dir = new Vector3(Math.cos(a), Math.sin(a), 0).applyQuaternion(qBase);
      dir.z = 0;
      if (dir.lengthSq() < 1e-6) dir.set(1, 0, 0);
      dir.normalize();

      // chained casts along the axis (duplicate radii reuse the previous landing)
      const pts: { p: Vector3; danger: number }[] = [];
      let prevZ = objPos.z;
      for (let k = 0; k < AR_N; k++) {
        const r = gizR + ARROW_PX[k]! * wpp;
        if (k > 0 && ARROW_PX[k] === ARROW_PX[k - 1]) {
          pts.push({ p: pts[k - 1]!.p, danger: pts[k - 1]!.danger });
          continue;
        }
        const tx = objPos.x + dir.x * r;
        const ty = objPos.y + dir.y * r;
        let c = castDown(tx, ty, prevZ, R);
        if (!c.est) c = castDown(tx, ty, prevZ, R, 8 * R);
        const z = c.est ? c.z : prevZ;
        if (c.est) prevZ = z;
        pts.push({ p: new Vector3(tx, ty, z + lift), danger: c.est ? 0 : 1 });
      }

      const geo = arrowGeos[i]!;
      const pos = geo.getAttribute('position') as BufferAttribute;
      const aTan = geo.getAttribute('aTangent') as BufferAttribute;
      const aSide = geo.getAttribute('aSide') as BufferAttribute;
      const aDanger = geo.getAttribute('aDanger') as BufferAttribute;
      const aWidth = geo.getAttribute('aWidth') as BufferAttribute;
      let lastTan = dir.clone();
      for (let k = 0; k < AR_N; k++) {
        const p = pts[k]!.p;
        const next = k < AR_N - 1 ? pts[k + 1]!.p : null;
        const t = next ? next.clone().sub(p) : null;
        const tan = t && t.lengthSq() > 1e-8 ? t.normalize() : lastTan;
        lastTan = tan;
        for (let s = 0; s < 2; s++) {
          const v = k * 2 + s;
          pos.setXYZ(v, p.x, p.y, p.z);
          aTan.setXYZ(v, tan.x, tan.y, tan.z);
          aSide.setX(v, s === 0 ? 1 : -1);
          aDanger.setX(v, pts[k]!.danger);
          aWidth.setX(v, ARROW_W[k]!);
        }
      }
      pos.needsUpdate = aTan.needsUpdate = aSide.needsUpdate = true;
      aDanger.needsUpdate = aWidth.needsUpdate = true;

      // hit-line follows the same cast polyline — grab what you see
      const hitGeo = arrowHitLines[i]!.geometry;
      const hitPos = hitGeo.getAttribute('position') as BufferAttribute;
      for (let k = 0; k < AR_N; k++) {
        const p = pts[k]!.p;
        hitPos.setXYZ(k, p.x, p.y, p.z);
      }
      hitPos.needsUpdate = true;
      hitGeo.computeBoundingSphere();
      // representative grab point for tests/UI (mid-shaft station)
      arrowGrab[i] = pts[2]!.p.clone();
    }
  };

  /** Position/orient the gizmo from the instance and publish $gizmoInfo. */
  const syncGizmo = (obj: Object3D, inst: Instance) => {
    const up = new Vector3().fromArray(inst.up);
    const qBase = baseQuatOf(up, inst.baseYaw);
    const pivot = new Vector3().fromArray(inst.position); // PARENT — on the surface
    baseGroup.position.copy(pivot);
    baseGroup.quaternion.copy(qBase);
    baseGroup.scale.setScalar(gizR);
    // Never (re)build the surface-hugging arrows mid-drag: rotObj/rotBase keep
    // the gizmo visible while baseYaw spins every frame, which would re-run the
    // ~20-40 GPU surface samples per frame (the "rotate cha" FPS drop). The rings
    // still spin (cheap transform below); onPointerUp forces one rebuild after.
    if (arrowsGroup.visible && getSampler() && !dragging) buildArrows(pivot, inst);
    // While a rotate handle is dragged the arrows are NOT rebuilt (above); their
    // yaw is driven through the ribbon shader's uSpin uniform in the rotObj/
    // rotBase $frame handler instead (the ribbon is world-space and ignores the
    // group matrix). onPointerUp forces one rebuild that re-hugs the terrain.
    // rotation rings: radius = the object's LARGE DIAGONAL / 2, wrapping the
    // lifted object's middle (child frame; pivot stays below on the surface)
    const ringR = Math.max(bboxDiagWorld / 2, 0.4);
    ringsGroup.position.copy(pivot).addScaledVector(up, inst.height + bboxHeight / 2);
    ringsGroup.quaternion.copy(qBase);
    ringsGroup.scale.setScalar(ringR);
    // height arrow ABOVE everything (object top or ring top, whichever higher)
    topGroup.position
      .copy(pivot)
      .addScaledVector(up, inst.height + Math.max(bboxHeight, bboxHeight / 2 + ringR) + gizR * 0.12);
    topGroup.quaternion.copy(qBase);
    topGroup.scale.setScalar(gizR);

    const handleWorld = (local: Vector3) =>
      local.clone().multiplyScalar(gizR).applyQuaternion(qBase).add(baseGroup.position);
    const topWorld = (local: Vector3) =>
      local.clone().multiplyScalar(gizR).applyQuaternion(qBase).add(topGroup.position);
    const handles: Record<string, Vec3> = {
      // real cast-built arrow points when available (grab what you see)
      dir0: (arrowGrab[0] ?? handleWorld(new Vector3(1.18, 0, 0))).toArray() as Vec3,
      dir1: (arrowGrab[1] ?? handleWorld(new Vector3(0, 1.18, 0))).toArray() as Vec3,
      dir2: (arrowGrab[2] ?? handleWorld(new Vector3(-1.18, 0, 0))).toArray() as Vec3,
      dir3: (arrowGrab[3] ?? handleWorld(new Vector3(0, -1.18, 0))).toArray() as Vec3,
      rotObj: handleWorld(new Vector3(Math.SQRT1_2, Math.SQRT1_2, 0)).toArray() as Vec3,
      rotBase: handleWorld(new Vector3(-Math.SQRT1_2, Math.SQRT1_2, 0)).toArray() as Vec3,
      free: handleWorld(new Vector3(-Math.SQRT1_2, -Math.SQRT1_2, 0)).toArray() as Vec3,
      height: topWorld(new Vector3(0, 0, 0.3)).toArray() as Vec3,
      // X-ring lies in the local YZ plane → grab point at (0, √½, √½)
      tiltX: ringsGroup.position
        .clone()
        .add(new Vector3(0, Math.SQRT1_2, Math.SQRT1_2).multiplyScalar(ringR).applyQuaternion(qBase))
        .toArray() as Vec3,
      // Y-ring lies in the local XZ plane → grab point at (√½, 0, √½)
      tiltY: ringsGroup.position
        .clone()
        .add(new Vector3(Math.SQRT1_2, 0, Math.SQRT1_2).multiplyScalar(ringR).applyQuaternion(qBase))
        .toArray() as Vec3,
    };
    atoms.set($gizmoInfo, {
      attached: true,
      ringCenter: baseGroup.position.toArray() as Vec3,
      ringRadius: gizR,
      quaternion: qBase.toArray() as Quat,
      handles,
    });
  };

  /** Screen angle of the pointer around the base center (for rotate handles). */
  const screenAngle = (ndc: { x: number; y: number }, camera: import('three').PerspectiveCamera) => {
    const c = baseGroup.position.clone().project(camera);
    return Math.atan2(ndc.y - c.y, (ndc.x - c.x) * camera.aspect);
  };

  // Enter/leave "danger" (object dragged into a hole / off the cloud → will be
  // deleted on release). Swap the object's OWN mesh material to the striped
  // hazardMat (zero extra geometry) and restore it when danger clears.
  const setDanger = (onNow: boolean, _inst?: Instance | null) => {
    danger = onNow;
    atoms.set($dragDanger, onNow);
    if (!onNow) {
      restoreHazard();
      return;
    }
    const src = selectedObject();
    if (!src || src === hazardObj) return;
    restoreHazard(); // clear a previous danger object, if any
    hazardObj = src;
    src.traverse((c) => {
      const m = c as Mesh;
      if ((m as any).isMesh) {
        hazardOrigMats.set(m, m.material);
        m.material = hazardMat;
      }
    });
  };

  // ------------------------------------------------------ guideline builder
  /** Render the ribbon from precomputed points (danger points shown red). */
  /** maskR > 0 hides the segment under the gizmo so the line STARTS at the arrows. */
  const drawPath = (
    pts: { p: Vector3; danger: boolean }[],
    originIdx: number,
    R: number,
    maskR = 0,
  ) => {
    if (pts.length < 2) {
      pathLine.visible = false;
      pathGeo.setDrawRange(0, 0);
      return;
    }
    const total = (pts.length - 1) * PATH_SUB;
    const originV = originIdx * PATH_SUB;
    const subPts: Vector3[] = [];
    const subDanger: number[] = [];
    for (let v = 0; v <= total; v++) {
      const seg = Math.min(Math.floor(v / PATH_SUB), pts.length - 2);
      const t = v / PATH_SUB - seg;
      subPts.push(new Vector3().lerpVectors(pts[seg]!.p, pts[seg + 1]!.p, t));
      subDanger.push(pts[seg]!.danger || pts[seg + 1]!.danger ? 1 : 0);
    }
    const pos = pathGeo.getAttribute('position') as BufferAttribute;
    const aTan = pathGeo.getAttribute('aTangent') as BufferAttribute;
    const aSide = pathGeo.getAttribute('aSide') as BufferAttribute;
    const aFade = pathGeo.getAttribute('aFade') as BufferAttribute;
    const aDanger = pathGeo.getAttribute('aDanger') as BufferAttribute;
    for (let v = 0; v <= total; v++) {
      const p = subPts[v]!;
      const tan = (v < total
        ? subPts[v + 1]!.clone().sub(p)
        : p.clone().sub(subPts[v - 1]!)
      ).normalize();
      const k = Math.abs(v - originV) / Math.max(originV, total - originV, 1);
      let fade = 0.95 * (1 - k) * (1 - k) + 0.04;
      if (maskR > 0) {
        // fade in from the arrow tip outward (no double band under the gizmo)
        const o = subPts[originV]!;
        // horizontal distance from the arrow tip — Z-up ⇒ ground plane is X/Y
        const d = Math.hypot(p.x - o.x, p.y - o.y);
        fade *= Math.min(Math.max((d - maskR * 0.85) / (maskR * 0.35), 0), 1);
      }
      for (let s = 0; s < 2; s++) {
        const i = v * 2 + s;
        pos.setXYZ(i, p.x, p.y, p.z);
        aTan.setXYZ(i, tan.x, tan.y, tan.z);
        aSide.setX(i, s === 0 ? 1 : -1);
        aFade.setX(i, fade);
        aDanger.setX(i, subDanger[v]!);
      }
    }
    pos.needsUpdate = aTan.needsUpdate = aSide.needsUpdate = true;
    aFade.needsUpdate = aDanger.needsUpdate = true;
    pathGeo.setDrawRange(0, total * 6);
    // arrows + guideline SHARE pathMat, so this must match the arrow width
    // (gizR-relative) — otherwise hovering, which draws the guideline, would
    // overwrite the shared uHalfWidth and visibly shrink the arrows.
    pathMat.uniforms.uHalfWidth!.value = gizR * 0.07;
    pathLine.visible = true;
  };

  /** Chained top-down casts from objPos both ways along dirH (holes flagged). */
  const castRuns = (objPos: Vector3, dirH: Vector3, step: number, R: number) => {
    const run = (dx: number, dy: number) => {
      const out: { p: Vector3; up: Vector3 | null; danger: boolean }[] = [];
      let prevZ = objPos.z;
      for (let i = 1; i <= PATH_N; i++) {
        const tx = objPos.x + dx * step * i;
        const ty = objPos.y + dy * step * i;
        let c = castDown(tx, ty, prevZ, R);
        if (!c.est) c = castDown(tx, ty, prevZ, R, 8 * R);
        if (c.est) {
          out.push({ p: new Vector3(tx, ty, c.z), up: c.est.normal.clone(), danger: false });
          prevZ = c.z;
        } else {
          out.push({ p: new Vector3(tx, ty, prevZ), up: null, danger: true });
        }
      }
      return out;
    };
    return { forward: run(dirH.x, dirH.y), backward: run(-dirH.x, -dirH.y) };
  };

  /** Hover preview: recompute when the aimed direction/position changed. */
  const updatePath = (objPos: Vector3, dirH: Vector3) => {
    const R = pickRadius();
    const step = Math.max(R * 0.9, gizR);
    const key = [
      Math.round(Math.atan2(dirH.y, dirH.x) / (Math.PI / 18)),
      Math.round(objPos.x / (step * 0.5)),
      Math.round(objPos.y / (step * 0.5)),
      step.toFixed(1),
    ].join(',');
    if (key === lastPathKey) {
      pathLine.visible = pathGeo.drawRange.count > 1;
      return;
    }
    lastPathKey = key;
    const lift = R * 0.2;
    const { forward, backward } = castRuns(objPos, dirH, step, R);
    const liftPt = (e: { p: Vector3; danger: boolean }) => ({
      p: e.p.clone().addScaledVector(UP, lift),
      danger: e.danger,
    });
    const pts = [
      ...backward.map(liftPt).reverse(),
      { p: objPos.clone().addScaledVector(UP, lift), danger: false },
      ...forward.map(liftPt),
    ];
    // hover: arrows are visible → the line starts right at the arrow tips
    drawPath(pts, backward.length, R, gizR * 1.4);
  };

  const hidePath = () => {
    pathLine.visible = false;
    lastPathKey = '';
  };

  // precalculated directed-drag rail: the object may ONLY travel on this line
  type RailSample = { s: number; pos: Vector3; quat: Quaternion; up: Vector3; danger: boolean };
  let rail: RailSample[] | null = null;
  let railStep = 1;

  /**
   * Precompute the full directed-drag rail ONCE at grab time: chained casts in
   * both directions, orientation parallel-transported sample by sample. The
   * ribbon is drawn once from it and stays FIXED for the whole drag.
   */
  const buildRail = (inst: Instance, objPos: Vector3, dirH: Vector3) => {
    const R = pickRadius();
    railStep = Math.max(R * 0.9, gizR);
    const { forward, backward } = castRuns(objPos, dirH, railStep, R);

    const chain = (
      run: { p: Vector3; up: Vector3 | null; danger: boolean }[],
      sign: 1 | -1,
    ): RailSample[] => {
      let q = new Quaternion().fromArray(inst.quaternion);
      let up = new Vector3().fromArray(inst.up);
      return run.map((e, i) => {
        if (e.up) {
          q = new Quaternion().setFromUnitVectors(up, e.up).multiply(q);
          up = e.up;
        }
        return { s: sign * (i + 1) * railStep, pos: e.p, quat: q.clone(), up: up.clone(), danger: e.danger };
      });
    };
    rail = [
      ...chain(backward, -1).reverse(),
      {
        s: 0,
        pos: objPos.clone(),
        quat: new Quaternion().fromArray(inst.quaternion),
        up: new Vector3().fromArray(inst.up),
        danger: false,
      },
      ...chain(forward, 1),
    ];

    const lift = R * 0.2;
    drawPath(
      rail.map((e) => ({ p: e.pos.clone().addScaledVector(UP, lift), danger: e.danger })),
      backward.length,
      R,
    );
  };

  // ------------------------------------------------------------ attach/sync
  effect([$selection, $instances, $cloudScenes, $scene, $pickRadius], () => {
    const scene = atoms.get($scene);
    const obj = selectedObject();
    const inst = selectedInstance();
    if (scene && obj && inst) {
      const b = instanceBBox(inst);
      const scale = atoms.get($objectScale) ?? 1;
      // footprint = horizontal (X,Y) half-extent; height = vertical Z extent
      bboxRadiusWorld = Math.max(
        (Math.max(b.max.x - b.min.x, b.max.y - b.min.y) / 2) * scale,
        0.4 * scale,
      );
      bboxHeight = (b.max.z - b.min.z) * scale;
      bboxDiagWorld = Math.hypot(b.max.x - b.min.x, b.max.y - b.min.y, b.max.z - b.min.z) * scale;
      gizR = computeGizR();
      syncGizmo(obj, inst);
      if (!mounted) {
        scene.add(baseGroup, topGroup, ringsGroup, arrowsGroup, pathLine);
        mounted = true;
      }
    } else {
      if (mounted) {
        baseGroup.parent?.remove(baseGroup);
        topGroup.parent?.remove(topGroup);
        ringsGroup.parent?.remove(ringsGroup);
        arrowsGroup.parent?.remove(arrowsGroup);
        pathLine.parent?.remove(pathLine);
        mounted = false;
      }
      // re-selecting must rebuild, not hit a stale key
      lastArrowObjKey = '';
      lastArrowViewKey = '';
      applyHover(null); // drop any hover highlight on deselect
      hidePath();
      setCursor('');
      atoms.set($gizmoInfo, {
        attached: false,
        ringCenter: null,
        ringRadius: 0,
        quaternion: null,
        handles: {},
      });
    }
  });

  // ------------------------------------------------------------- pointerdown
  const onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0 || dragging || !mounted) return;
    const camera = atoms.get($camera);
    const obj = selectedObject();
    const inst = selectedInstance();
    const ndc = toNdc(e);
    if (!camera || !obj || !inst || !ndc) return;
    // the OBJECT wins when it is in front of the gizmo along the ray: clicking
    // the visible object body always free-drags it, handles only win when they
    // are actually in front (or the object was missed). Depth-correct pick.
    const { pick, objHit } = pickHandleHit(ndc, camera, obj);
    if (!pick && !objHit) return;
    applyHover(null); // grabbing clears the hover highlight
    let grabbed: HandleName;
    if (objHit && (!pick || objHit.distance < pick.distance)) {
      grabbed = 'free';
    } else if (pick) {
      grabbed = pick.object.userData.handle as HandleName;
    } else {
      return;
    }

    e.stopPropagation(); // placement/orbit never see this pointerdown
    dragging = true;
    handle = grabbed;
    dragId = inst.id;
    start = { ...inst };
    smoothPos = new Vector3().fromArray(inst.position); // PARENT pivot
    smoothQuat = obj.quaternion.clone();
    curUp = new Vector3().fromArray(inst.up);
    mouseNdc = ndc;
    startNdc = { ...ndc };
    dirty = false;
    atoms.set($editorMode, 'drag');
    setCursor('grabbing');

    const qBase = baseQuatOf(curUp, inst.baseYaw);
    if (grabbed.startsWith('dir')) {
      const i = Number(grabbed[3]);
      const a = (i * Math.PI) / 2;
      const axis = new Vector3(Math.cos(a), Math.sin(a), 0).applyQuaternion(qBase);
      axis.z = 0; // surface travel is planned horizontally (top-down casts)
      dragDir = axis.lengthSq() > 1e-6 ? axis.normalize() : new Vector3(1, 0, 0);
      baseGroup.visible = topGroup.visible = ringsGroup.visible = arrowsGroup.visible = false;
      // PRECALCULATE the rail once — anchored at the PARENT pivot's foot
      buildRail(inst, new Vector3().fromArray(inst.position), dragDir);
    } else if (grabbed === 'free') {
      dragDir = null;
      baseGroup.visible = topGroup.visible = ringsGroup.visible = arrowsGroup.visible = false;
      hidePath();
    } else if (grabbed === 'rotObj' || grabbed === 'rotBase') {
      startAngle = screenAngle(ndc, camera);
      // seen from the up side, screen-CCW = positive yaw; flipped when underneath
      rotSign = curUp.dot(new Vector3().subVectors(camera.position, obj.position)) >= 0 ? 1 : -1;
    } else if (grabbed === 'height') {
      raycaster.setFromCamera(new Vector2(ndc.x, ndc.y), camera);
      tStart = axisParam(raycaster.ray, new Vector3().fromArray(inst.position), curUp);
    } else if (grabbed === 'tiltX' || grabbed === 'tiltY') {
      const local = grabbed === 'tiltX' ? new Vector3(1, 0, 0) : new Vector3(0, 1, 0);
      tiltAxis = local.applyQuaternion(qBase).normalize();
      tiltCenter = ringsGroup.position.clone();
      const hitP = raycaster.ray.intersectPlane(
        new Plane().setFromNormalAndCoplanarPoint(tiltAxis, tiltCenter),
        new Vector3(),
      );
      tiltV0 = hitP && hitP.distanceToSquared(tiltCenter) > 1e-8
        ? hitP.sub(tiltCenter).normalize()
        : null;
    }
    // grab offset for surface drags: cursor ↦ horizontal plane through the PIVOT
    const pivot0 = new Vector3().fromArray(inst.position);
    const plane = new Plane(UP, -pivot0.z);
    const hit = raycaster.ray.intersectPlane(plane, new Vector3());
    grabOffset = hit ? pivot0.sub(hit) : new Vector3();
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!mounted) return;
    const ndc = toNdc(e);
    if (ndc) {
      mouseNdc = ndc;
      dirty = true;
    } else if (!dragging) {
      mouseNdc = null;
    }
  };

  const onPointerUp = () => {
    if (!dragging) return;
    const deleteIt = danger;
    const id = dragId;
    dragging = false;
    handle = null;
    dragId = null;
    start = null;
    dragDir = null;
    rail = null;
    smoothPos = smoothQuat = null;
    baseGroup.visible = topGroup.visible = ringsGroup.visible = arrowsGroup.visible = true;
    // clear the spin transform the rot-drag applied to the arrows, so the
    // rebuilt (re-sampled) geometry below lands in absolute world coords again.
    arrowsGroup.position.set(0, 0, 0);
    arrowsGroup.quaternion.identity();
    hidePath();
    setDanger(false);
    setCursor('');
    atoms.set($editorMode, 'selected');
    if (deleteIt && id) {
      emit($deleteRequest, { id }); // dropped in a hole → delete
    } else if (id) {
      // Committed object moved / rotated / lifted via the gizmo. The POC gizmo
      // drives everything through $transformRequest, which only mutates the
      // $instances map — it never told React. Fire ifc-object-edited so the
      // "Save Design Data" button marks the scene dirty (edits persist only via
      // the explicit Save snapshot; there is no auto-save).
      const cid = maps.get($instances, id)?.committedId;
      if (cid && typeof document !== 'undefined') {
        document.dispatchEvent(
          new CustomEvent('ifc-object-edited', { detail: { id: cid } }),
        );
      }
    }
    // arrows were hidden during the drag (rebuild skipped) — re-sync them to
    // the object's NEW position, otherwise they linger at the old spot
    const obj = selectedObject();
    const inst = selectedInstance();
    if (obj && inst && mounted) {
      forceArrows = true; // one rebuild against the new landing surface
      lastPathKey = ''; // and let the hover guideline recompute (baseYaw changed)
      syncGizmo(obj, inst);
    }
  };

  // ------------------------------------------------------------- frame loop
  on($frame, (f) => {
    if (hazardObj) hazardMat.uniforms.uTime!.value = f?.time ?? 0;
    if (!mounted) return;
    const camera = atoms.get($camera);
    const obj = selectedObject();
    const inst = selectedInstance();
    if (!camera || !obj || !inst || !getSampler()) return;

    // keep the base plate at a constant pixel size while the camera moves
    if (!dragging) {
      const r = computeGizR();
      if (Math.abs(r - gizR) / gizR > 0.01) {
        gizR = r;
        syncGizmo(obj, inst);
      }
    }

    if (dragging && handle && dragId && start && smoothPos && smoothQuat) {
      if (!dirty || !mouseNdc) return;
      dirty = false;
      const R = pickRadius();
      raycaster.setFromCamera(new Vector2(mouseNdc.x, mouseNdc.y), camera);

      if (handle.startsWith('dir') && dragDir && rail && rail.length > 1) {
        // ---- DIRECTED drag: slide on the PRECALCULATED rail — no re-casting.
        // Cursor maps through a FIXED plane at the start height, projected onto
        // the locked axis, then clamped to the rail's precomputed extent.
        const startPos = new Vector3().fromArray(start.position);
        const planeHit = raycaster.ray.intersectPlane(
          new Plane(UP, -startPos.z),
          new Vector3(),
        );
        if (!planeHit) return;
        const sRaw = planeHit.add(grabOffset).sub(startPos).dot(dragDir);
        const sMin = rail[0]!.s;
        const sMax = rail[rail.length - 1]!.s;
        const s = Math.min(Math.max(sRaw, sMin), sMax);

        // locate the segment and interpolate position/orientation on the rail
        const f = (s - sMin) / railStep;
        const i0 = Math.min(Math.floor(f), rail.length - 2);
        const t = f - i0;
        const a = rail[i0]!;
        const b = rail[i0 + 1]!;
        const pos = new Vector3().lerpVectors(a.pos, b.pos, t);
        const quat = a.quat.clone().slerp(b.quat, t);
        const up = a.up.clone().lerp(b.up, t).normalize();
        const inDanger = t < 0.5 ? a.danger : b.danger;

        smoothPos.lerp(pos, 0.5);
        smoothQuat.slerp(quat, 0.5);
        curUp.copy(up);
        emit($transformRequest, {
          id: dragId,
          position: smoothPos.toArray() as Vec3,
          quaternion: smoothQuat.toArray() as Quat,
          up: up.toArray() as Vec3,
        });
        setDanger(inDanger, inst);
        return;
      }

      if (handle === 'free') {
        // ---- FREE drag: horizontal target → top-down cast → land -----------
        // cursor ↦ horizontal plane at height zz
        const targetAt = (zz: number): Vector3 | null => {
          const planeHit = raycaster.ray.intersectPlane(new Plane(UP, -zz), new Vector3());
          if (!planeHit) return null;
          return planeHit.add(grabOffset);
        };
        let target = targetAt(smoothPos.z);
        if (!target) return;
        {
          const pull = new Vector3(target.x - smoothPos.x, target.y - smoothPos.y, 0);
          if (pull.lengthSq() > 1e-4) {
            dragDir = dragDir ? dragDir.lerp(pull.normalize(), 0.25).normalize() : pull.normalize();
          }
        }

        let cast = castDown(target.x, target.y, smoothPos.z, R);
        if (!cast.est) {
          // widened window: recover from danger over much higher/lower terrain
          cast = castDown(target.x, target.y, smoothPos.z, R, 8 * R);
        }
        if (cast.est && Math.abs(cast.z - smoothPos.z) > R * 0.5) {
          // parallax correction: re-map the cursor at the landed height so the
          // object stays under the pointer after climbing/descending
          const target2 = targetAt(cast.z);
          if (target2) {
            const cast2 = castDown(target2.x, target2.y, cast.z, R, 2 * R);
            if (cast2.est) {
              target = target2;
              cast = cast2;
            }
          }
        }
        const { est, z } = cast;
        if (!est) {
          // HOLE: slide flat at the current height, hazard state on, delete on release
          smoothPos.lerp(new Vector3(target.x, target.y, smoothPos.z), 0.45);
          emit($transformRequest, { id: dragId, position: smoothPos.toArray() as Vec3 });
          setDanger(true, inst);
          return;
        }
        setDanger(false);

        // parallel-transport the orientation from the old up to the new one
        const newUp = est.normal.clone();
        const deltaAlign = new Quaternion().setFromUnitVectors(curUp, newUp);
        const targetQuat = deltaAlign.clone().multiply(smoothQuat);
        smoothPos.lerp(new Vector3(target.x, target.y, z), 0.45);
        smoothQuat.slerp(targetQuat, 0.45);
        curUp.copy(newUp);
        emit($transformRequest, {
          id: dragId,
          position: smoothPos.toArray() as Vec3,
          quaternion: smoothQuat.toArray() as Quat,
          up: newUp.toArray() as Vec3,
        });
        return;
      }

      if (handle === 'rotObj' || handle === 'rotBase') {
        // ---- yaw around the up axis ----------------------------------------
        const dA = (screenAngle(mouseNdc, camera) - startAngle) * rotSign;
        const yaw = start.baseYaw + dA;
        // Spin the surface-hugging arrows WITH the base plate. They are NOT
        // rebuilt mid-drag (that would re-run the GPU surface samples every
        // frame — the "rotate cha" FPS drop), and the ribbon is world-space and
        // bypasses the model matrix so a group transform is a no-op. Feed the
        // yaw into the ribbon shader instead; buildArrows resets it to identity
        // on the post-drag rebuild that re-hugs the arrows to the terrain.
        const spinAxis = new Vector3().fromArray(start.up);
        pathMat.uniforms.uSpin.value.setFromMatrix4(
          new Matrix4().makeRotationAxis(spinAxis, dA),
        );
        pathMat.uniforms.uSpinPivot.value.fromArray(start.position);
        if (handle === 'rotBase') {
          emit($transformRequest, { id: dragId, baseYaw: yaw });
        } else {
          const spin = new Quaternion().setFromAxisAngle(spinAxis, dA);
          const q = spin.multiply(new Quaternion().fromArray(start.quaternion));
          emit($transformRequest, { id: dragId, quaternion: q.toArray() as Quat, baseYaw: yaw });
        }
        return;
      }

      if (handle === 'height') {
        // ---- CHILD lift along up: the object rises, the PIVOT stays planted
        const t = axisParam(raycaster.ray, new Vector3().fromArray(start.position), curUp);
        emit($transformRequest, { id: dragId, height: start.height + (t - tStart) });
        return;
      }

      if (handle === 'tiltX' || handle === 'tiltY') {
        // ---- traditional rotation ring: signed angle in the ring's plane ---
        // around the base X/Y axis, pivot AND height untouched (child only)
        if (!tiltV0) return;
        const hitP = raycaster.ray.intersectPlane(
          new Plane().setFromNormalAndCoplanarPoint(tiltAxis, tiltCenter),
          new Vector3(),
        );
        if (!hitP) return;
        const v = hitP.sub(tiltCenter);
        if (v.lengthSq() < 1e-8) return;
        v.normalize();
        const dA = Math.atan2(new Vector3().crossVectors(tiltV0, v).dot(tiltAxis), tiltV0.dot(v));
        const q = new Quaternion()
          .setFromAxisAngle(tiltAxis, dA)
          .multiply(new Quaternion().fromArray(start.quaternion));
        emit($transformRequest, { id: dragId, quaternion: q.toArray() as Quat });
        return;
      }
      return;
    }

    // ------------------------------------------------------------- hovering
    if (atoms.get($editorMode) !== 'selected' || !mouseNdc) {
      if (gizmoCursor) {
        setCursor('');
        gizmoCursor = false;
      }
      applyHover(null);
      hidePath();
      return;
    }
    const { pick: hoverPick, objHit: hoverObjHit } = pickHandleHit(mouseNdc, camera, obj);
    if (!hoverPick || (hoverObjHit && hoverObjHit.distance < hoverPick.distance)) {
      // nothing, or the object itself is in front → placement's hover rules
      if (gizmoCursor) {
        setCursor('');
        gizmoCursor = false;
      }
      applyHover(null);
      hidePath();
      return;
    }
    const h = hoverPick.object.userData.handle as HandleName;
    applyHover(h); // brighten the hovered handle
    setCursor(h === 'free' ? 'move' : 'grab');
    gizmoCursor = true;
    if (h.startsWith('dir')) {
      // preview the travel line of that arrow (both directions)
      const qBase = baseQuatOf(new Vector3().fromArray(inst.up), inst.baseYaw);
      const i = Number(h[3]);
      const a = (i * Math.PI) / 2;
      const axis = new Vector3(Math.cos(a), Math.sin(a), 0).applyQuaternion(qBase);
      axis.z = 0;
      if (axis.lengthSq() > 1e-6) {
        // hover guideline starts at the PARENT pivot's foot
        updatePath(new Vector3().fromArray(inst.position), axis.normalize());
      }
    } else {
      hidePath();
    }
  });

  // ---------------------------------------------------------------- wiring
  // The surface sampler is owned by surfaceSystem ($surfaceSampler); read via
  // getSampler(). No sampler lifecycle here anymore.
  window.addEventListener('pointerdown', onPointerDown, true);
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);

  onDispose(() => {
    window.removeEventListener('pointerdown', onPointerDown, true);
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    baseGroup.parent?.remove(baseGroup);
    topGroup.parent?.remove(topGroup);
    ringsGroup.parent?.remove(ringsGroup);
    arrowsGroup.parent?.remove(arrowsGroup);
    pathLine.parent?.remove(pathLine);
    restoreHazard();
    hazardMat.dispose();
    setCursor('');
    pathGeo.dispose();
    arrowGeos.forEach((g) => g.dispose());
    pathMat.dispose();
    hazardMat.dispose();
    outlineGeo.dispose();
  });
});
